import * as React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import FullWidthCarousel from 'LuxKit/Carousel/FullWidthCarousel'
import { mediaQueryUp } from 'components/utils/breakpoint'
import useMerchandiseCategoriesList from 'hooks/Merchandise/useMerchandiseCategoriesList'
import MerchandiseCarouselSection from '../MerchandiseCarouselSection'
import MerchandiseCategoryCard from '../MerchandiseCards/MerchandiseCategoryCard'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import PillLoadingBox from 'LuxKit/Pill/PillLoadingBox'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import { useIntl } from 'react-intl'

const GridContainer = styled(FullWidthCarousel)`
  > div {
    grid-template-rows: 1fr 1fr 1fr;

    ${mediaQueryUp.tablet} {
      grid-template-rows: unset;
    }
  }
`

const StyledLoadingBox = styled(LoadingBox)`
  width: ${rem(85)};
  height: ${rem(85)};

  ${mediaQueryUp.tablet} {
    width: ${rem(180)};
    height: ${rem(180)};
  }
`

const LoadingContainer = styled(VerticalSpacer)`
  display: grid;
  gap: ${rem(16)};
  grid-template-columns: ${rem(85)} 1fr;
  width: ${rem(280)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr;
    grid-template-rows: ${rem(180)} 1fr;
    flex-direction: column;
    grid-gap: ${rem(12)};
    width: ${rem(180)};
  }
`

interface Props {
  title: React.ReactNode
  subtitle?: React.ReactNode
  viewAllTo?: string
  /**
   * Supply filters to have the carousel manage
   * the list of categories that will be shown
   */
  filters?: App.MerchandiseCategoryFilters
  /**
   * Provide your own list of category ids
   */
  overrideList?: Array<string>
  /**
   * Provide your own state for whether the list is fetching or not
   */
  overrideFetching?: boolean
}

function MerchandiseCategoriesCarousel(props: Props) {
  const intl = useIntl()
  const { title, filters, subtitle, viewAllTo, overrideList, overrideFetching } = props

  const [categoriesList, fetchingList] = useMerchandiseCategoriesList(filters, { disabled: !!overrideList })

  const finalList = overrideList ?? categoriesList
  const finalFetching = overrideFetching ?? fetchingList

  return <MerchandiseCarouselSection
    title={title}
    subtitle={subtitle}
    viewAllTo={viewAllTo}
    viewAllText={intl.formatMessage({
      id: 'O-345LiAFAG350HF9HIV',
      defaultMessage: 'View all',
      description: 'View all button text for the categories carousel in the merchandise shop section',
    })}
    trackingName="categories"
  >
    <GridContainer snap="start" itemsPerArrow={6}>
      {finalFetching && Array.from({ length: 11 }).map((_, index) => (
        <LoadingContainer gap={16} key={index}>
          <StyledLoadingBox />
          <VerticalSpacer gap={4}>
            <TextLoadingBox typography="body-large" width="8ch" />
            <PillLoadingBox width={96} />
          </VerticalSpacer>
        </LoadingContainer>
      ))}
      {!finalFetching && finalList.map((categoryId, index) => (
        <MerchandiseCategoryCard
          key={categoryId}
          id={categoryId}
          index={index}
        />
      ))}
    </GridContainer>
  </MerchandiseCarouselSection>
}

export default MerchandiseCategoriesCarousel
